import { Avatar, Group, Table } from "@mantine/core";
import { type GetPlayerResponse } from "#app/routes/players.server.ts";
import { PlayerRating } from "./player-rating";

type Props = {
  player: FormattedPlayer;
};

export function PlayerStatsLeaguesTable({ player }: Props) {
  const isGoalkeeper = player.SquadPlayer[0]?.position === "Goalkeeper";

  if (!player.SquadPlayer[0]) {
    return <div>Player not found</div>;
  }

  return (
    <Table.ScrollContainer minWidth={540}>
      <Table variant="vertical" layout="fixed" withTableBorder>
        <Table.Tbody>
          <Table.Tr>
            <Table.Th w={160}>League</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => (
                <Group gap="xs">
                  <Avatar src={stat.League.logo} size="sm" /> {stat.League.name}
                </Group>
              )}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Team</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => (
                <Group gap="xs">
                  <Avatar src={stat.Team.logo} size="sm" /> {stat.Team.name}
                </Group>
              )}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Season</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.season}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Rating</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => (
                <PlayerRating rating={stat.games_rating} />
              )}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Appearances</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.games_appearances}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Starts</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.games_lineups}
            />
          </Table.Tr>

          {!isGoalkeeper && (
            <>
              <Table.Tr>
                <Table.Th>Shots</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.shots_total}
                />
              </Table.Tr>

              <Table.Tr>
                <Table.Th>Shots on target</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.shots_on}
                />
              </Table.Tr>

              <Table.Tr>
                <Table.Th>Goals</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.goals_total}
                />
              </Table.Tr>

              <Table.Tr>
                <Table.Th>Penalty scored</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.penalty_scored}
                />
              </Table.Tr>

              <Table.Tr>
                <Table.Th>Penalty missed</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.penalty_missed}
                />
              </Table.Tr>

              <Table.Tr>
                <Table.Th>Assists</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.goals_assists}
                />
              </Table.Tr>
            </>
          )}

          {isGoalkeeper && (
            <>
              <Table.Tr>
                <Table.Th>Goals Saved</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.goals_saves}
                />
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Penalties Saved</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.penalty_saved}
                />
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Goals conceded</Table.Th>
                <TableValues
                  stats={player.PlayerStatistics}
                  renderValue={(stat) => stat.goals_conceded}
                />
              </Table.Tr>
            </>
          )}

          <Table.Tr>
            <Table.Th>Passes</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.passes_total}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Key passes</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.passes_key}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Tackles</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.tackles_total}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Duels Won</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.duels_won}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Fouls Drawn</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.fouls_drawn}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Fouls Committed</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.fouls_committed}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Yellow Cards</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.cards_yellow}
            />
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Red Cards</Table.Th>
            <TableValues
              stats={player.PlayerStatistics}
              renderValue={(stat) => stat.cards_red}
            />
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

type FormattedPlayer = Omit<GetPlayerResponse, "PlayerStatistics"> & {
  PlayerStatistics: FormattedPlayerStats[];
};

type FormattedPlayerStats = Omit<
  GetPlayerResponse["PlayerStatistics"][number],
  "games_rating"
> & {
  games_rating: number | undefined;
};

function TableValues({
  stats,
  renderValue,
}: {
  stats: FormattedPlayerStats[];
  renderValue: (value: FormattedPlayerStats) => React.ReactNode;
}) {
  return stats.map((stat) => (
    <Table.Td key={`${stat.leagueId}_${stat.teamId}_${stat.season}`}>
      {renderValue(stat)}
    </Table.Td>
  ));
}
